import DisplayModeDropdown from "~/components/build/Show/components/Sidebar/DisplayModeDropdown";
import { StepTree } from "~/components/build/Show/components/Sidebar/StepTree";
import { StepSearch } from "~/components/build/Show/components/Sidebar/StepSearch";
import Icon from "~/components/shared/Icon";
import { useHotkeys } from "react-hotkeys-hook";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useRef } from "react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useTreeNodes } from "~/components/build/Show/components/Sidebar/useTreeNodes";
import {
  DisplayMode,
  useFilterStore,
} from "~/components/build/Show/components/Sidebar/useFilterStore";

const SIDEBAR_HEADER_HEIGHT = 42;
const SIDEBAR_HEADER_EXPANDED_HEIGHT = 80;

export default function Sidebar() {
  const { build } = useBuild();

  const displayMode = useFilterStore((state) => state.displayMode || DisplayMode.State);
  const { isLoading, nodes } = useTreeNodes({ displayMode });

  const sidebarRef = useRef<HTMLDivElement>(null);
  const isSearchOpen = useBuildStore((state) => state.isSearchOpen);
  const setSearchOpen = useBuildStore((state) => state.setSearchOpen);

  useHotkeys("s", () => setSearchOpen(true), { preventDefault: true, enabled: !isSearchOpen });
  useHotkeys("escape", () => setSearchOpen(false));

  const headerHeight = isSearchOpen ? SIDEBAR_HEADER_EXPANDED_HEIGHT : SIDEBAR_HEADER_HEIGHT;

  return (
    <div
      id="sidebar"
      className="no-scrollbar absolute -left-2 -right-2 z-0 -mt-px flex h-full flex-col overflow-y-auto px-2"
      style={{ contain: "strict" }}
      data-testid="sidebar"
      ref={sidebarRef}
    >
      <div className="sticky top-0 z-30 flex flex-col gap-2 bg-white pb-1.5">
        <div className="mt-2 flex items-center gap-1.5">
          <div className="flex-1">
            <DisplayModeDropdown />
          </div>

          <button
            aria-checked={isSearchOpen}
            onClick={() => setSearchOpen(!isSearchOpen)}
            title="Search for step (S)"
            data-testid="toggle-search-button"
            className="hover:bg-text-600 group inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 font-medium transition-all hover:bg-purple-100 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50 aria-checked:bg-purple-100 aria-checked:text-purple-600"
          >
            <Icon icon="heroicons/16/solid/magnifying-glass" className="size-4" />
          </button>
        </div>

        {isSearchOpen && (
          <div className="flex items-center gap-1.5">
            <StepSearch />
          </div>
        )}
      </div>

      <div className="flex flex-col">
        {/* Show skeleton state when either steps are loading or nodes are being calculated */}
        {Features.ProgressiveBuildShow && isLoading ? (
          <ul className="flex flex-col gap-2">
            {Array.from({ length: Math.min(build.statistics?.stepsCount || 0, 10) }).map(
              (_item, index) => (
                <li key={index} className="h-[1.75rem] w-full animate-pulse rounded bg-gray-200" />
              ),
            )}
          </ul>
        ) : (
          <div className="flex flex-col">
            <StepTree
              displayMode={displayMode}
              nodes={nodes}
              scrollRef={sidebarRef}
              offsetTop={headerHeight}
            />
          </div>
        )}
      </div>
    </div>
  );
}
